import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, TrendingUp, Calendar, UserCheck, Menu, LogOut, Home, ClipboardList, BarChart3, Newspaper, Bot, CalendarClock, KeyRound, Loader2, X, ShieldCheck, Download } from 'lucide-react';import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [agenteStats, setAgenteStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [downloadsTotal, setDownloadsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ senha_atual: '', nova_senha: '', confirmar: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.nova_senha.length < 8) { setPwError('A nova senha deve ter pelo menos 8 caracteres.'); return; }
    if (pwForm.nova_senha !== pwForm.confirmar) { setPwError('A confirmação não confere com a nova senha.'); return; }
    setPwSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/change-password`, { senha_atual: pwForm.senha_atual, nova_senha: pwForm.nova_senha }, { withCredentials: true });
      toast({ title: 'Senha alterada', description: 'Use a nova senha no próximo login.' });
      setPwModal(false);
      setPwForm({ senha_atual: '', nova_senha: '', confirmar: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Não foi possível alterar a senha.');
    } finally {
      setPwSaving(false);
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Verificar autenticação via cookie httpOnly
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        // Limpa eventual token antigo do localStorage (migração)
        localStorage.removeItem('admin_token');
        fetchData();
      } catch (error) {
        logger.error('Sessão inválida:', error);
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };

    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes, agenteRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leads/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leads?limit=10`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/agente/stats`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setRecentLeads(leadsRes.data);
      setAgenteStats(agenteRes.data);

      try {
        const sv = await axios.get(`${BACKEND_URL}/api/downloads/surveys?limit=1`, { withCredentials: true });
        setDownloadsTotal(sv.data.total || 0);
      } catch (_) { /* opcional */ }
    } catch (error) {
      logger.error('Erro ao buscar dados:', error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar as informações.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    toast({
      title: "Logout realizado",
      description: "Até logo!",
    });
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      {pwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="alterar-senha-modal">
          <div className="bg-gray-900 rounded-2xl border border-yellow-500/30 shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-yellow-400" />
                <h2 className="text-lg font-bold text-white">Alterar senha</h2>
              </div>
              <button onClick={() => setPwModal(false)} className="text-gray-400 hover:text-white" data-testid="fechar-alterar-senha-btn"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={changePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1">Senha atual</label>
                <input type="password" value={pwForm.senha_atual} onChange={(e) => setPwForm(f => ({ ...f, senha_atual: e.target.value }))} data-testid="senha-atual-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-yellow-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1">Nova senha</label>
                <input type="password" value={pwForm.nova_senha} onChange={(e) => setPwForm(f => ({ ...f, nova_senha: e.target.value }))} data-testid="nova-senha-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-yellow-500" />
                <p className="text-xs text-gray-500 mt-1">Mínimo de 8 caracteres. Use letras, números e um símbolo.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1">Confirmar nova senha</label>
                <input type="password" value={pwForm.confirmar} onChange={(e) => setPwForm(f => ({ ...f, confirmar: e.target.value }))} data-testid="confirmar-senha-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-yellow-500" />
              </div>
              {pwError && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="senha-error">{pwError}</div>}
              <button type="submit" disabled={pwSaving} data-testid="salvar-senha-btn" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 text-black font-bold transition-all">
                {pwSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                {pwSaving ? 'Salvando...' : 'Salvar nova senha'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-white p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          <AdminNav active="dashboard" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
              <p className="text-gray-400">Visão geral do sistema</p>
            </div>
            <button
              onClick={() => { setPwError(''); setPwModal(true); }}
              data-testid="abrir-alterar-senha-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-yellow-400 border border-yellow-500/30 font-semibold text-sm transition-colors"
            >
              <KeyRound className="w-4 h-4" /> Alterar senha
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="glass-dark p-6 rounded-xl border border-yellow-500/20">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-8 h-8 text-yellow-400" />
                <span className="text-xs text-gray-400">TOTAL</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats?.total_leads || 0}</div>
              <div className="text-sm text-gray-400">Total de Leads</div>
            </div>

            <div className="glass-dark p-6 rounded-xl border border-green-500/20">
              <div className="flex items-center justify-between mb-4">
                <Calendar className="w-8 h-8 text-green-400" />
                <span className="text-xs text-gray-400">MÊS</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats?.leads_este_mes || 0}</div>
              <div className="text-sm text-gray-400">Leads Este Mês</div>
            </div>

            <div className="glass-dark p-6 rounded-xl border border-blue-500/20">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-blue-400" />
                <span className="text-xs text-gray-400">GRUPO</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats?.leads_mentoria_grupo || 0}</div>
              <div className="text-sm text-gray-400">Mentoria em Grupo</div>
            </div>

            <div className="glass-dark p-6 rounded-xl border border-purple-500/20">
              <div className="flex items-center justify-between mb-4">
                <UserCheck className="w-8 h-8 text-purple-400" />
                <span className="text-xs text-gray-400">INDIVIDUAL</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats?.leads_treinamento_individual || 0}</div>
              <div className="text-sm text-gray-400">Treinamento Individual</div>
            </div>

            <div className="glass-dark p-6 rounded-xl border border-orange-500/20" data-testid="dashboard-downloads-leads">
              <div className="flex items-center justify-between mb-4">
                <Download className="w-8 h-8 text-orange-400" />
                <span className="text-xs text-gray-400">DOWNLOADS</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{downloadsTotal}</div>
              <div className="text-sm text-gray-400">Leads Captados (Downloads)</div>
            </div>
          </div>

          {/* Agente Técnico Stats */}
          <div className="glass-dark p-6 rounded-xl border border-cyan-500/20 mb-8" data-testid="agente-stats-card">
            <div className="flex items-center gap-3 mb-4">
              <Bot className="w-6 h-6 text-cyan-400" />
              <h2 className="text-lg font-bold text-white">Agente Técnico WING</h2>
              <button onClick={() => navigate('/admin/agente')} data-testid="agente-ver-conversas-btn" className="text-cyan-400 hover:text-cyan-300 text-xs ml-auto font-bold">Ver conversas →</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold text-white" data-testid="agente-total-acessos">{agenteStats?.total_acessos ?? 0}</div>
                <div className="text-xs text-gray-400">Acessos totais</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white" data-testid="agente-usuarios-unicos">{agenteStats?.usuarios_unicos ?? 0}</div>
                <div className="text-xs text-gray-400">Usuários únicos</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white" data-testid="agente-total-perguntas">{agenteStats?.total_perguntas ?? 0}</div>
                <div className="text-xs text-gray-400">Perguntas feitas</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white" data-testid="agente-perguntas-hoje">{agenteStats?.perguntas_hoje ?? 0}</div>
                <div className="text-xs text-gray-400">Perguntas hoje</div>
              </div>
            </div>
          </div>

          {/* Recent Leads */}
          <div className="glass-dark p-6 rounded-xl border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Últimos Leads</h2>
              <button
                onClick={() => navigate('/admin/leads')}
                className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
              >
                Ver todos →
              </button>
            </div>

            {recentLeads.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Nenhum lead cadastrado ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Nome</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Telefone</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Tipo</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4 text-white">{lead.nome}</td>
                        <td className="py-3 px-4 text-gray-300">{lead.email}</td>
                        <td className="py-3 px-4 text-gray-300">{lead.telefone}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            lead.tipo_mentoria === 'mentoria_grupo' 
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {lead.tipo_mentoria === 'mentoria_grupo' ? 'Grupo' : 'Individual'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {new Date(lead.data_criacao).toLocaleDateString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;